import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter as FilterIcon, MoreVertical, Plus } from "lucide-react";

import { Filter } from "../../../ui/Filter";
import { CAMPO } from "../../../ui/Filter/sizes";
import { Input } from "../../../ui/Form/Input";
import { Select } from "../../../ui/Form/Select";
import { Table } from "../../../ui/Table";
import { formatMoney } from "../../../../services/functions";

const SORTABLE_COLUMN_KEYS = {
  Nome: "promotionName",
  Banhos: "baths",
  Adicionais: "additionals",
  Desconto: "discount",
  "Preço c/ desconto": "priceWithDiscount",
  Status: "status",
};

const CAMPOS_NUMERICOS = ["discount", "priceWithDiscount", "baths", "additionals"];

/*
 * Zero e "esse pacote nao tem banho". Indefinido e "a API ainda nao me
 * contou" — enquanto a versao antiga da API estiver no ar, o campo nao vem.
 * Mostrar 0 nos dois casos faria a tela afirmar uma coisa que nao sabe.
 */
function quantidade(valor) {
  return Number.isFinite(Number(valor)) ? Number(valor) : "—";
}

function compara(a, b, campo) {
  const x = a?.[campo];
  const y = b?.[campo];

  if (x === y) return 0;
  if (x === null || x === undefined || x === "") return 1;
  if (y === null || y === undefined || y === "") return -1;

  if (CAMPOS_NUMERICOS.includes(campo)) return Number(x) - Number(y);

  return String(x).localeCompare(String(y), "pt-BR", { sensitivity: "base" });
}

/**
 * Aba "Pacotes disponiveis".
 *
 * Era tabela do Material UI, sem cabecalho laranja e sem ordenacao. A lista
 * vem inteira da API (sem paginacao no servidor), entao a ordenacao e feita
 * aqui mesmo e vale para a lista toda.
 */
export function AvailablePackages({
  packages,
  onSearch,
  onChangeActive,
  onCreate,
  onSell,
  onToggleActive,
}) {
  const [nome, setNome] = useState("");
  const [status, setStatus] = useState("ATIVO");
  const [sort, setSort] = useState({ sortBy: null, sortOrder: "asc" });
  const [menuAberto, setMenuAberto] = useState(null);

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    onSearch(nome);
  };

  const handleClear = () => {
    setNome("");
    setStatus("ATIVO");
    setSort({ sortBy: null, sortOrder: "asc" });
    onChangeActive(true);
  };

  const handleSortColumn = (sortBy) => {
    setSort((atual) => ({
      sortBy,
      sortOrder: atual.sortBy === sortBy && atual.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const linhas = useMemo(() => {
    const lista = Array.isArray(packages) ? packages : [];
    if (!sort.sortBy) return lista;

    const fator = sort.sortOrder === "asc" ? 1 : -1;
    return [...lista].sort((a, b) => compara(a, b, sort.sortBy) * fator);
  }, [packages, sort]);

  return (
    <div className="flex w-full flex-col gap-5 pt-5">
      <Filter.Root onSubmit={handleSubmit}>
        <Filter.Container>
          <Input.Root
            label="Nome"
            placeholder="Nome do pacote"
            style={CAMPO.busca}
            value={nome}
            onChange={(valor) => {
              setNome(valor);
              onSearch(valor);
            }}
          />

          <Select.Root
            label="Status"
            style={CAMPO.medio}
            value={status}
            onChange={(valor) => {
              setStatus(valor);
              onChangeActive(valor === "ATIVO");
            }}
          >
            <Select.Option value="ATIVO">Ativo</Select.Option>
            <Select.Option value="INATIVO">Inativo</Select.Option>
          </Select.Root>
        </Filter.Container>

        <Filter.Actions>
          <Filter.Action>
            <Filter.ActionIcon icon={FilterIcon} />
            Filtrar
          </Filter.Action>

          <Filter.Action
            outline
            type="button"
            name="clear-available-packages"
            onClick={handleClear}
          >
            Limpar
          </Filter.Action>

          <Filter.Action type="button" name="new-package" onClick={onCreate}>
            <Filter.ActionIcon icon={Plus} />
            Novo pacote
          </Filter.Action>
        </Filter.Actions>
      </Filter.Root>

      <Table.Root name="table-available-packages" config>
        <Table.Header
          /*
           * Distribuicao: o Nome passou a poder crescer ate 560px em vez de
           * parar em 400. O resto do espaco ia todo para a coluna da
           * engrenagem (minmax(40px, 1fr)), que e o que deixava aquele vazio
           * enorme no meio da tabela.
           */
          columns={[
            "Nome:minmax(220px, 560px)",
            "Banhos:95px",
            "Adicionais:105px",
            "Desconto:100px",
            "Preço c/ desconto:130px",
            "Status:95px",
            "Ações:70px",
          ]}
          sortableConfig={{
            sortBy: sort.sortBy,
            sortOrder: sort.sortOrder,
            onSort: handleSortColumn,
            columnKeys: SORTABLE_COLUMN_KEYS,
          }}
        />

        <Table.Content isLoading={!packages}>
          {linhas.map((row) => (
            <Table.Line key={row?.id || row?.promotionName}>
              <Table.Item title={row?.promotionName}>{row?.promotionName}</Table.Item>
              <Table.Item className="text-right">{quantidade(row?.baths)}</Table.Item>
              <Table.Item className="text-right">{quantidade(row?.additionals)}</Table.Item>
              <Table.Item className="text-right">{row?.discount}</Table.Item>
              <Table.Item className="text-right">
                R$ {formatMoney(row?.priceWithDiscount)}
              </Table.Item>
              <Table.Item>{row?.status}</Table.Item>
              <Table.Item>
                <div className="relative">
                  <button
                    type="button"
                    title="Ações"
                    className="text-gray-700 hover:text-scale-600"
                    onClick={() => setMenuAberto(menuAberto === row?.id ? null : row?.id)}
                  >
                    <MoreVertical size={18} />
                  </button>

                  {menuAberto === row?.id && (
                    <div className="absolute right-0 top-6 z-20 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                      <Link
                        to={`/pacotes/edit/${row?.id}`}
                        className="block px-3 py-2 text-sm text-gray-800 no-underline hover:bg-gray-100"
                      >
                        Editar
                      </Link>

                      {row?.status === "ATIVO" && (
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                          onClick={() => {
                            setMenuAberto(null);
                            onSell(row.id);
                          }}
                        >
                          Vender
                        </button>
                      )}

                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                        onClick={() => {
                          setMenuAberto(null);
                          onToggleActive(row.status, row.id);
                        }}
                      >
                        {row?.status === "ATIVO" ? "Inativar" : "Ativar"}
                      </button>
                    </div>
                  )}
                </div>
              </Table.Item>
            </Table.Line>
          ))}
        </Table.Content>
      </Table.Root>
    </div>
  );
}
